import { subtractWorkingDays } from '../_helpers/working-days';
import { ShippingConfigContext, ShippingConfigType } from './shipping-config.interface';

/**
 * Termin-Vorbelegung einer Versandkonfiguration — ohne Angular-Abhängigkeiten,
 * damit das alte Formular (`_shared/shipping-config-form/`), der geführte
 * Versandziel-Dialog und der Versandzeitpunkt-Dialog exakt dieselben Defaults
 * verwenden. Der Versandziel-Dialog fragt die Termine nicht mehr ab, sondern
 * legt sie beim Anlegen still nach diesen Regeln fest.
 */

/**
 * Standard-Vorlauf für den Versand-Vorschlag (Termin minus N WERKTAGE).
 * Spiegelt ShippingConfig::DEFAULT_SHIPPING_DELAY_DAYS im Backend. PickPoint:
 * Versand- und Staging-Datum 5 Werktage vor dem Abholzeitpunkt.
 */
export const STAGING_DELAY_WORKING_DAYS = 5;

/**
 * Vorlauf des „normalen" Versanddatums für Nicht-PickPoint-Typen im
 * FEST-Datum-Modus: 14 KALENDERtage vor dem Auftragsdatum (= genau 2 Wochen).
 */
export const DEFAULT_LEAD_DAYS = 14;

/**
 * Fallback-Vorlauf der OFFSET-Regel („X Werktage vor Auftrag"), falls der
 * Context (Gruppen-Default) noch nicht geladen ist. 12 WERKTAGE ≈ 2 Wochen
 * (14 Kalendertage − 2 Sonntage). Authoritativ ist GROUP_SHIPPING_OFFSET_DAYS
 * im Backend (default_shipping_offset_days im Context).
 */
export const DEFAULT_OFFSET_WORKING_DAYS = 12;

export interface ScheduleDefaultsInput {
  /** Kontext-Antwort des Backends (Auftragsgruppe, Fenster-Defaults). */
  context: ShippingConfigContext | null | undefined;
  /** Auftragsdatum als Bezug (ISO-String). */
  referenceDate?: string;
  /** Abholtermin als datetime-local ("YYYY-MM-DDTHH:mm") — nur PickPoint. */
  pickupDate?: string;
}

/**
 * Vorbelegung der Termin-Felder. `null` bei `earliestOffsetDays` bzw.
 * `stagingOffsetDays` heißt bewusst „nicht anfassen" — der latente Wert im
 * Formular bleibt dann stehen (Verhalten wie bisher).
 */
export interface ScheduleDefaults {
  shippingDateMode: 'fixed' | 'offset' | 'window';
  shippingOffsetDays: number;
  earliestOffsetDays: number | null;
  shippingDate: string;
  stagingShipMode: 'fixed' | 'offset' | 'standard';
  stagingOffsetDays: number | null;
  stagingShipReadyAt: string;
}

/** Lokales Datum im Format "YYYY-MM-DD" (type="date"). */
export function formatLocalDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Lokales Datum + Uhrzeit im Format "YYYY-MM-DDTHH:mm" (type="datetime-local"). */
export function toDatetimeLocalFromDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${formatLocalDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Wandelt einen ISO-8601-String in das von <input type="datetime-local">
 * erwartete Format um. Direktes Abschneiden auf 16 Zeichen funktioniert nur,
 * wenn der String bereits ohne Zonen-Offset in lokaler Zeit ist — andernfalls
 * verschiebt sich der Wert um die TZ-Differenz.
 */
export function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return toDatetimeLocalFromDate(d);
}

/** Datum minus N KALENDERtage, lokal formatiert. "" wenn nicht parsebar. */
export function dateLocalMinusDays(basis: string, days: number): string {
  if (!basis) return '';
  const d = new Date(basis);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() - days);
  return formatLocalDate(d);
}

/**
 * Default-Abholtermin einer PickPoint-Config: Auftragsdatum minus 1 Stunde.
 * "" wenn kein Bezugsdatum vorliegt — dann setzt das Backend beim Anlegen
 * seinen eigenen Default.
 */
export function defaultPickupDate(referenceDate?: string): string {
  if (!referenceDate) return '';
  const ref = new Date(referenceDate);
  if (Number.isNaN(ref.getTime())) return '';
  ref.setHours(ref.getHours() - 1);
  return toDatetimeLocalFromDate(ref);
}

/**
 * Default-Vorlaufzeit (Werktage) für die „X Werktage vorher"-Regel des
 * normalen Versands. Quelle: Auftragsgruppe (Context). PickPoint rechnet
 * konzeptionell gegen den Abholtermin und nutzt denselben 5-Werktage-Vorlauf
 * wie der Staging-Versand.
 */
export function defaultShippingOffsetDays(
  type: ShippingConfigType,
  context: ShippingConfigContext | null | undefined,
): number {
  if (type === 'pickpoint') return STAGING_DELAY_WORKING_DAYS;

  const v = context?.default_shipping_offset_days;
  return v != null ? v : DEFAULT_OFFSET_WORKING_DAYS;
}

/**
 * Default fürs „normale" Versanddatum (FEST-Datum-Modus) nach Typ:
 *  - PickPoint: 5 Werktage vor dem Abholzeitpunkt (sonst Auftragsdatum)
 *  - sonst:     14 Kalendertage vor dem Auftragsdatum — ABER nur bei EXTERNEN
 *    Aufträgen OHNE Material-Versand-Prio. Bei internen Aufträgen und bei
 *    externen mit material_shipping-Prio bleibt das Feld leer (= Versand am
 *    Auftragsdatum), da dort kein Vorlauf gebraucht wird.
 */
export function shipDateDefaultForType(
  type: ShippingConfigType,
  input: ScheduleDefaultsInput,
): string {
  if (type === 'pickpoint') {
    const basis = input.pickupDate || input.referenceDate || '';
    if (!basis) return '';
    const d = new Date(basis);
    if (Number.isNaN(d.getTime())) return '';
    return formatLocalDate(subtractWorkingDays(d, STAGING_DELAY_WORKING_DAYS));
  }

  const isInternal = input.context?.project_is_internal === true;
  const hasMaterialShippingPriority = input.context?.ticket_has_material_shipping_priority === true;
  if (isInternal || hasMaterialShippingPriority) return '';

  return dateLocalMinusDays(input.referenceDate || '', DEFAULT_LEAD_DAYS);
}

/**
 * Vorschlag für den frühesten Staging-Versand-Zeitpunkt: Bezug ist der
 * Abholzeitpunkt (an dem Tag entsteht der Abholauftrag für den Techniker),
 * ersatzweise das Auftragsdatum. Minus 5 Werktage, Uhrzeit 09:00.
 */
export function computeStagingShipReady(input: ScheduleDefaultsInput): string {
  const basis = input.pickupDate || input.referenceDate || '';
  if (!basis) return '';
  const d = new Date(basis);
  if (Number.isNaN(d.getTime())) return '';
  const shifted = subtractWorkingDays(d, STAGING_DELAY_WORKING_DAYS);
  shifted.setHours(9, 0, 0, 0);
  return toDatetimeLocalFromDate(shifted);
}

/**
 * Komplette Termin-Vorbelegung für einen Versandtyp (Neu-Anlage und
 * Typ-Wechsel).
 *
 * Normaler Versand: Regel als Standard („X Werktage vor Auftragsdatum") mit dem
 * Gruppen-Default aus dem Context. Technikerversand auf EXTERNEN Tickets
 * bekommt stattdessen das Versandfenster „Zeitraum vor Auftrag" (frühestens/
 * spätestens aus dem Context, 8–10 WT); interne Tickets bleiben beim
 * Offset-Default. PickPoint fällt immer auf 'offset' zurück (Fenster ist dort
 * nicht wählbar).
 *
 * Staging: PickPoint bekommt die Offset-Regel mit 5 Werktagen, alle anderen
 * Typen „Standard" (keine Verzögerung, Versand nach KW-Zuordnung).
 */
export function scheduleDefaultsFor(
  type: ShippingConfigType,
  input: ScheduleDefaultsInput,
): ScheduleDefaults {
  const win = input.context?.technician_window_defaults;
  const useWindow = type === 'technician_shipping'
    && input.context?.project_is_internal === false
    && !!win;

  const isPickpoint = type === 'pickpoint';

  return {
    shippingDateMode: useWindow ? 'window' : 'offset',
    shippingOffsetDays: useWindow ? win!.latest : defaultShippingOffsetDays(type, input.context),
    earliestOffsetDays: useWindow ? win!.earliest : null,
    shippingDate: shipDateDefaultForType(type, input),
    stagingShipMode: isPickpoint ? 'offset' : 'standard',
    stagingOffsetDays: isPickpoint ? STAGING_DELAY_WORKING_DAYS : null,
    stagingShipReadyAt: isPickpoint ? computeStagingShipReady(input) : '',
  };
}
